import type { GroupSummary } from '@ralph/contracts'
import clsx from 'clsx'
import { Plus, Search, X } from 'lucide-react'
import { useId, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiRequestError } from '@/api/client.ts'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { CreateGroupDialog } from '@/components/groups/GroupDialogs.tsx'
import { GroupPanel } from '@/components/groups/GroupPanel.tsx'
import { riseDelay, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Button, EmptyState, Input, Spinner } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useGroup, useGroups } from '@/hooks/use-groups.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * Groups (PRD-26 D-28): a page for everyone, in the People page's shape.
 *
 * A list on the left and one group on the right, with the selection in the URL — `/groups/:id` —
 * so a notification ("Legal Handbook is now shared with Finance EMEA") can land on the group it
 * is about, and the back button does what it says. Below `lg` the detail is a full-width push.
 *
 * Administrators see every group with *All · Yours · No managers*, and create them here; everyone
 * else sees *Your groups* and *All groups*. A group's existence is not a secret (D-7): anybody may
 * find one and see who to ask to join it.
 */

type Filter = 'all' | 'mine' | 'unmanaged'

export const GroupsPage = () => {
  const fmt = useI18n()
  const t = fmt.t.groups
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const session = useSession()
  const isAdmin = session.data?.user?.roles.includes('admin') ?? false
  const searchId = useId()

  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<Filter>(isAdmin ? 'all' : 'mine')
  const [creating, setCreating] = useState(false)

  const list = useGroups({
    q: q.trim() === '' ? undefined : q.trim(),
    scope: filter === 'mine' ? 'mine' : 'all',
    limit: 200
  })
  const detail = useGroup(groupId ?? null)
  const missing = detail.error instanceof ApiRequestError && detail.error.status === 404

  const items: GroupSummary[] = (list.data?.items ?? []).filter(
    (group) => filter !== 'unmanaged' || group.managers.length === 0
  )
  const filters: Filter[] = isAdmin ? ['all', 'mine', 'unmanaged'] : ['mine', 'all']
  const labels = isAdmin ? t.page.adminFilters : t.page.filters

  const emptyState = () => {
    if (q.trim() !== '') return <EmptyState title={t.page.noMatch(q.trim())} body={t.page.noMatchBody} />
    if (filter === 'mine') return <EmptyState title={t.page.emptyMine} body={t.page.emptyMineBody} />
    return <EmptyState title={t.page.empty} body={isAdmin ? t.page.emptyAdmin : t.page.emptyBody} />
  }

  return (
    <div className="fade-in flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 border-b border-rule px-5 pt-5 sm:px-8">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
          {/* A basis, so on a phone the actions wrap below the lead rather than squeezing it
              into a narrow column beside them. */}
          <div className="min-w-0 flex-1 basis-[16rem]">
            <h1 className="display text-[30px] sm:text-[38px]">{t.page.title}</h1>
            <p className="mt-[7px] max-w-[560px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">
              {isAdmin ? t.page.leadAdmin : t.page.lead}
            </p>
          </div>
          {isAdmin && (
            <Button variant="primary" icon={<Plus size={14} aria-hidden />} onClick={() => setCreating(true)}>
              {t.page.newGroup}
            </Button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 pb-4">
          <div className="relative min-w-[13rem] flex-1 basis-[16rem]">
            <label htmlFor={searchId} className="sr-only">
              {t.page.searchLabel}
            </label>
            <Search
              size={14}
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
            />
            <Input
              id={searchId}
              value={q}
              placeholder={t.page.searchPlaceholder}
              className="pl-8"
              onChange={(event) => setQ(event.target.value)}
            />
            {q !== '' && (
              <button
                type="button"
                aria-label={t.page.clearSearch}
                onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-fg-subtle hover:text-fg"
              >
                <X size={13} aria-hidden />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {filters.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
                className={clsx(
                  'inline-flex h-[28px] items-center rounded-full border px-3 text-[12px] tracking-[-0.01em]',
                  'transition-[color,background-color,border-color] duration-200',
                  filter === value
                    ? 'border-rule-strong bg-card-solid font-semibold text-fg'
                    : 'border-rule bg-card text-fg-muted hover:border-rule-strong hover:text-fg'
                )}
              >
                {labels[value]}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 lg:divide-x lg:divide-rule">
        <div
          className={clsx(
            'min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-8 lg:max-w-[26rem] lg:px-5',
            groupId && 'hidden lg:block'
          )}
        >
          {list.isLoading && (
            <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
              <Spinner /> {t.page.loading}
            </p>
          )}
          {list.isError && <EmptyState title={t.page.failed} body={t.page.failedBody} />}
          {!list.isLoading && !list.isError && items.length === 0 && emptyState()}

          <ul className="space-y-2">
            {items.map((group, index) => (
              <li key={group.id}>
                <button
                  type="button"
                  aria-current={groupId === group.id ? 'page' : undefined}
                  onClick={() => navigate(`/groups/${group.id}`)}
                  className="w-full text-left"
                >
                  <StudioRow
                    style={riseDelay(index)}
                    className={clsx(groupId === group.id && 'border-rule-strong bg-card-solid')}
                  >
                    <div className="flex items-center gap-3">
                      <GroupAvatar group={group} size={28} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13.5px] font-semibold tracking-[-0.014em]">{group.name}</p>
                        <p className="readout truncate text-fg-subtle">
                          {t.page.rowMeta(fmt, group.memberCount, group.isManager, group.isMember)}
                        </p>
                      </div>
                    </div>
                    <p
                      className={clsx(
                        'mt-2 truncate text-[12.5px]',
                        group.managers.length === 0 ? 'text-alert' : 'text-fg-muted'
                      )}
                    >
                      {group.managers.length === 0
                        ? t.page.noManagersList
                        : t.page.managedBy(
                            fmt,
                            group.managers.map((manager) => manager.displayName)
                          )}
                    </p>
                  </StudioRow>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className={clsx('min-h-0 flex-1 overflow-y-auto', !groupId && 'hidden lg:block')}>
          <GroupPanel
            group={detail.data}
            isLoading={detail.isLoading && Boolean(groupId)}
            missing={missing}
            onRetry={groupId && detail.isError && !missing ? () => void detail.refetch() : undefined}
            {...(groupId ? { onBack: () => navigate('/groups') } : {})}
          />
        </div>
      </div>

      {creating && <CreateGroupDialog onClose={() => setCreating(false)} />}
    </div>
  )
}
